import { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const TestimonialFAQSection = ({ faqs = [] }) => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [mobileSlide, setMobileSlide] = useState(0);

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'CEO, TechStart Inc.',
      content: 'Easy Trademark Filing made the trademark registration process incredibly smooth. Their team was professional, responsive, and guided us through every step.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop'
    },
    {
      name: 'David Chen',
      role: 'Founder, Creative Studio',
      content: 'Outstanding service! They handled our copyright and trademark needs with expertise. The entire process was transparent and efficient.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Small Business Owner',
      content: 'I was worried about the complexity of trademark registration, but Easy Trademark Filing simplified everything. Great value for money and exceptional support.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop'
    },
    {
      name: 'Michael Johnson',
      role: 'VP Legal, InnovateCo',
      content: 'Professional, knowledgeable, and reliable. They helped us secure multiple trademarks and provided excellent ongoing monitoring services.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop'
    },
    {
      name: 'Jennifer Lee',
      role: 'Entrepreneur',
      content: 'The team at Easy Trademark Filing exceeded my expectations. Fast, professional service with clear communication every step of the way.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop'
    },
    {
      name: 'Robert Taylor',
      role: 'Business Consultant',
      content: 'Highly recommend! Their expertise in trademark law is evident, and they made the whole process stress-free for my clients.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop'
    }
  ];

  // Create extended array for continuous scroll
  const extendedTestimonials = [...testimonials, ...testimonials, ...testimonials];

  useEffect(() => {
    const interval = setInterval(() => {
      setScrollPosition((prev) => {
        const cardHeight = 280; // Approximate height of each testimonial card
        const newPosition = prev + 1;
        // Reset when we've scrolled through one full set
        if (newPosition >= testimonials.length * cardHeight) {
          return 0;
        }
        return newPosition;
      });
    }, 30); // Smooth continuous scroll

    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Auto-advance mobile carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setMobileSlide((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  // Default FAQs if none provided
  const defaultFaqs = [
    {
      question: 'How long does trademark registration take?',
      answer: 'The trademark registration process typically takes 8-12 months from filing to approval, though timelines can vary based on USPTO workload and any office actions that may arise.'
    },
    {
      question: 'What can be trademarked?',
      answer: 'You can trademark words, phrases, logos, symbols, designs, sounds, colors, or any combination that identifies and distinguishes your goods or services from those of others.'
    },
    {
      question: 'How much does trademark registration cost?',
      answer: 'Our service fees range from $45 to $349 depending on the package you choose, plus USPTO government filing fees. We offer transparent pricing with no hidden costs or surprises.'
    },
    {
      question: 'Do I need a trademark attorney?',
      answer: 'While not legally required, professional assistance significantly increases approval chances and helps avoid costly mistakes in the application process. Our experienced attorneys handle complex requirements efficiently.'
    },
    {
      question: 'What if my application is rejected?',
      answer: 'We provide expert responses to USPTO office actions and work diligently to overcome objections. Our team has extensive experience in addressing examiner concerns and securing trademark registrations.'
    },
    {
      question: 'Can I trademark a name that is already in use?',
      answer: 'It depends on several factors including the similarity of the marks, the goods/services involved, and geographic areas. We conduct comprehensive searches to assess availability and advise on your specific situation.'
    },
    {
      question: 'What is the difference between TM and ®?',
      answer: 'TM indicates an unregistered trademark claim, while ® can only be used after your trademark is officially registered with the USPTO. Using ® without registration can result in penalties.'
    },
    {
      question: 'How do I maintain my trademark registration?',
      answer: 'You must file maintenance documents between the 5th-6th year after registration and renewal applications every 10 years thereafter. We can help manage these requirements to keep your trademark active.'
    },
    {
      question: 'What happens after my trademark is registered?',
      answer: 'After registration, you receive exclusive nationwide rights to use your mark. We recommend ongoing monitoring services to identify potential infringements and maintain your trademark protection.'
    },
    {
      question: 'Can I register an international trademark?',
      answer: 'Yes, U.S. trademark registration can serve as a basis for international protection through the Madrid Protocol and other treaties. We can guide you through international trademark registration processes.'
    }
  ];

  const displayFaqs = faqs.length > 0 ? faqs : defaultFaqs;

  return (
    <section className="py-20 bg-white" data-testid="testimonial-faq-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">What Our Clients Say</h2>
          <p className="text-xl text-gray-600">Trusted by businesses across the nation</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
          {/* FAQs - Left Side */}
          <div>
            <h3 className="text-3xl font-bold mb-8">Frequently Asked Questions</h3>
            <Accordion type="single" collapsible className="space-y-4">
              {displayFaqs.map((faq, idx) => (
                <AccordionItem 
                  key={idx} 
                  value={`item-${idx}`}
                  className="bg-gray-50 border border-gray-200 rounded-xl px-6"
                  data-testid={`faq-item-${idx}`}
                >
                  <AccordionTrigger className="text-left font-semibold text-lg hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Testimonials - Right Side */}
          <div className="relative">
            {/* Desktop - 3 visible testimonials with continuous scroll */}
            <div className="hidden md:block">
              <div className="h-[840px] overflow-hidden relative">
                <div 
                  className="space-y-6 transition-transform"
                  style={{ transform: `translateY(-${scrollPosition}px)` }}
                >
                  {extendedTestimonials.map((testimonial, idx) => (
                    <div
                      key={idx}
                      className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 shadow-lg"
                      data-testid={`testimonial-${idx}`}
                    >
                      <div className="flex items-center space-x-4 mb-4">
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                        <div>
                          <p className="font-bold text-lg text-gray-900">{testimonial.name}</p>
                          <p className="text-gray-600 text-sm">{testimonial.role}</p>
                        </div>
                      </div>
                      
                      <div className="flex mb-4">
                        {[...Array(testimonial.rating)].map((_, starIdx) => (
                          <Star key={starIdx} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                      
                      <p className="text-gray-700 leading-relaxed italic">
                        "{testimonial.content}"
                      </p>
                    </div>
                  ))}
                </div>
                
                {/* Gradient overlays for smooth visual effect */}
                <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-white to-transparent pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
              </div>
            </div>

            {/* Mobile - Auto-sliding Carousel */}
            <div className="md:hidden relative overflow-hidden">
              <div className="relative" style={{ minHeight: '300px' }}>
                {testimonials.map((testimonial, idx) => (
                  <div
                    key={idx}
                    className="absolute inset-0 transition-all duration-700 ease-in-out"
                    style={{
                      opacity: mobileSlide === idx ? 1 : 0,
                      transform: mobileSlide === idx ? 'translateX(0)' : 'translateX(60px)',
                      pointerEvents: mobileSlide === idx ? 'auto' : 'none',
                    }}
                    data-testid={`testimonial-faq-mobile-${idx}`}
                  >
                    <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 shadow-lg h-full">
                      <div className="flex items-center space-x-4 mb-4">
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="w-14 h-14 rounded-full object-cover"
                        />
                        <div>
                          <p className="font-bold text-base text-gray-900">{testimonial.name}</p>
                          <p className="text-gray-600 text-sm">{testimonial.role}</p>
                        </div>
                      </div>
                      
                      <div className="flex mb-3">
                        {[...Array(testimonial.rating)].map((_, starIdx) => (
                          <Star key={starIdx} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                      
                      <p className="text-gray-700 leading-relaxed italic">
                        "{testimonial.content}"
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-center mt-4 space-x-2">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setMobileSlide(idx)}
                    className={`rounded-full transition-all ${
                      mobileSlide === idx
                        ? 'w-8 h-3 bg-gradient-to-r from-red-600 to-orange-500'
                        : 'w-3 h-3 bg-gray-300'
                    }`}
                    aria-label={`Go to testimonial ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialFAQSection;
